import React, { useRef } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import Fader from '../../Loaders/Fader';

import useOutsideClick from '../../../../util/useOutsideClick';

import CloseIcon from '../../../../assets/images/icons/close.svg';
import ArrowUpIcon from '../../../../assets/images/icons/arrow-up.svg';
import ArrowDownIcon from '../../../../assets/images/icons/arrow-down.svg';
import './PollWindow.scss';

const PollWindow = ({ data, isOpen, onCloseHandler }) => {
  const windowRef = useRef();

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  const ageGroups = [
    '0_4',
    '5_9',
    '10_14',
    '15_19',
    '20_24',
    '25_29',
    '30_34',
    '35_39',
    '40_44',
    '45_49',
    '50_54',
    '55_59',
    '60_64',
    '65_69',
    '70_74',
    '75_79',
    '80_84',
    '85_plus',
  ];

  let ageChartData = [];
  let occupationsData = [];

  if (data) {
    ageChartData = ageGroups.map((group) => {
      const label = group === '85_plus' ? '85+' : group.replace('_', '-');
      return {
        age: label,
        Female: data[`female_${group}`],
        Male: data[`male_${group}`],
      };
    });

    occupationsData = [
      {
        label: 'All Occupations',
        current: data.occ_all_occupations_2024,
        prev: data.occ_all_occupations,
      },
      {
        label: 'Arts, Culture, Recreation/Sports',
        current: data.occ_arts_culture_2024,
        prev: data.occ_arts_culture_rec_sport,
      },
      {
        label: 'Business, Finance & Admin',
        current: data.occ_bus_fin_admin_2024,
        prev: data.occ_bus_fin_admin,
      },
      {
        label: 'Education, Law, Social, Government',
        current: data.occ_soc_sci_edu_govt_2024,
        prev: data.occ_edu_law_social_govt,
      },
      {
        label: 'Health',
        current: data.occ_health_2024,
        prev: data.occ_health,
      },
      {
        label: 'Management',
        current: data.occ_management_2024,
        prev: data.occ_management,
      },
      {
        label: 'Manufacturing & Utilities',
        current: data.occ_manufacture_utilities_2024,
        prev: data.occ_manufacture_utilities,
      },
      {
        label: 'Natural & Applied Sciences',
        current: data.occ_sciences_2024,
        prev: data.occ_natural_app_sciences,
      },
      {
        label: 'Primary Industries',
        current: data.occ_primary_industries_2024,
        prev: data.occ_primary_industries,
      },
      {
        label: 'Sales & Service',
        current: data.occ_sales_service_2024,
        prev: data.occ_sales_service,
      },
      {
        label: 'Trades & Equipment Operators',
        current: data.occ_trades_transport_2024,
        prev: data.occ_trades_equipment_oper,
      },
    ];
  }

  const renderChange = (current, prev) => {
    const diff = current - prev;
    if (diff > 0) {
      return (
        <span className="poll__change poll__change_pos">
          <img src={ArrowUpIcon} className="poll__arrow" />+{diff}
        </span>
      );
    }
    if (diff < 0) {
      return (
        <span className="poll__change poll__change_neg">
          <img src={ArrowDownIcon} className="poll__arrow" />
          {diff}
        </span>
      );
    }
    return <span>0</span>;
  };

  return (
    <div className="login-wrap user-add-wrap">
      <div className="login-view">
        <div ref={windowRef} className="login-box poll">
          <img src={CloseIcon} onClick={onCloseHandler} className="close-icon" />
          <h2 className="poll__title">Poll Data {data ? data.poll : ''}</h2>
          {data ? (
            <div className="poll-wrapper">
              <PerfectScrollbar
                options={{
                  wheelPropagation: false,
                  autoHide: false,
                }}
              >
                <div className="poll__block">
                  <h3 className="poll__subtitle">General Data</h3>
                  <p className="poll__info">
                    <span className="poll__label">Total Population: </span>
                    {data.total_population}
                  </p>
                  <p className="poll__info">
                    <span className="poll__label">Total Households: </span>
                    {data.total_household}
                  </p>
                  <p className="poll__info">
                    <span className="poll__label">Average Household Size: </span>
                    {data.average_household_size}
                  </p>
                </div>
                <div className="poll__block">
                  <h3 className="poll__subtitle">Gender Breakdown</h3>
                  <p className="poll__info">
                    <span className="poll__label">Male: </span>
                    {data.male}
                  </p>
                  <p className="poll__info">
                    <span className="poll__label">Female: </span>
                    {data.female}
                  </p>
                </div>
                <div className="poll__block">
                  <h3 className="poll__subtitle">Age Group Breakdown</h3>
                  <div className="poll__chart">
                    <ResponsiveContainer>
                      <BarChart data={ageChartData} barGap={0} margin={{ left: -10, bottom: 10 }}>
                        <XAxis dataKey="age" interval={0} angle={-45} textAnchor="end" />
                        <YAxis />
                        <Tooltip />
                        <Legend verticalAlign="top" />
                        <Bar dataKey="Male" fill="#42a5f5" />
                        <Bar dataKey="Female" fill="#f06292" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="poll__block">
                  <h3 className="poll__subtitle">Occupation Data</h3>
                  <table className="poll__table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>2024</th>
                        <th>2021</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {occupationsData.map(({ label, current, prev }) => (
                        <tr key={label}>
                          <td>{label}</td>
                          <td>{current}</td>
                          <td>{prev}</td>
                          <td>{renderChange(current, prev)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </PerfectScrollbar>
            </div>
          ) : (
            <div className="wrap">
              <Fader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PollWindow;
